import React, { useState, useCallback } from 'react';

const QnACard = ({ questions, onSubmit, onDismiss }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  // answers[i] = { selected: [...], custom: '' }
  const [answers, setAnswers] = useState(
    questions.map(() => ({ selected: [], custom: '' }))
  );

  const current = questions[currentIndex];
  const answer = answers[currentIndex];
  const isSingleSelect = current.type === 'single_select';
  const allowCustom = current.allow_custom !== false; // default true
  const isLast = currentIndex === questions.length - 1;

  const toggleOption = useCallback((option) => {
    setAnswers(prev => {
      const updated = [...prev];
      const a = { ...updated[currentIndex] };
      if (isSingleSelect) {
        a.selected = a.selected[0] === option ? [] : [option];
        a.custom = '';
      } else {
        a.selected = a.selected.includes(option)
          ? a.selected.filter(o => o !== option)
          : [...a.selected, option];
      }
      updated[currentIndex] = a;
      return updated;
    });
  }, [currentIndex, isSingleSelect]);

  const setCustom = useCallback((value) => {
    setAnswers(prev => {
      const updated = [...prev];
      const a = { ...updated[currentIndex] };
      a.custom = value;
      if (isSingleSelect && value) {
        a.selected = [];
      }
      updated[currentIndex] = a;
      return updated;
    });
  }, [currentIndex, isSingleSelect]);

  const hasAnswer = answer.selected.length > 0 || answer.custom.trim().length > 0;

  const goNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const goPrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleSubmit = () => {
    const formatted = questions.map((q, i) => {
      const a = answers[i];
      let answerText = '';
      if (a.custom.trim()) {
        answerText = a.custom.trim();
      } else if (a.selected.length > 0) {
        answerText = a.selected.join(', ');
      } else {
        answerText = '(skipped)';
      }
      return `Q: ${q.question}\nA: ${answerText}`;
    }).join('\n\n');
    onSubmit(formatted);
  };

  const handleDismiss = () => {
    onDismiss('[User dismissed — do not proceed, wait for next instruction]');
  };

  return (
    <div
      className="w-full overflow-hidden"
      style={{
        borderRadius: 'var(--radius-3xl, 24px)',
        backgroundColor: 'var(--bg-secondary)',
      }}
    >
      {/* Header: question + pagination + close */}
      <div className="flex items-center justify-between px-5 pt-4 pb-2">
        <h3
          className="text-base font-medium flex-1 pr-4"
          style={{ color: 'var(--text-primary)' }}
        >
          {current.question}
        </h3>
        <div className="flex items-center gap-2 flex-shrink-0">
          {questions.length > 1 && (
            <div className="flex items-center gap-1 text-xs" style={{ color: 'var(--text-secondary, #9ca3af)' }}>
              <button
                onClick={goPrev}
                disabled={currentIndex === 0}
                className="p-1 rounded transition-opacity disabled:opacity-30"
                style={{ color: 'var(--text-secondary)' }}
              >
                &lt;
              </button>
              <span>{currentIndex + 1} of {questions.length}</span>
              <button
                onClick={goNext}
                disabled={isLast}
                className="p-1 rounded transition-opacity disabled:opacity-30"
                style={{ color: 'var(--text-secondary)' }}
              >
                &gt;
              </button>
            </div>
          )}
          <button
            onClick={handleDismiss}
            className="p-1 rounded transition-opacity hover:opacity-70"
            style={{ color: 'var(--text-secondary)' }}
            aria-label="Dismiss"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      {/* Options list */}
      <div className="px-5 pb-2">
        {current.options.map((option, i) => {
          const isSelected = answer.selected.includes(option);
          const isGreyedOut = isSingleSelect && answer.custom.trim().length > 0;

          return (
            <button
              key={i}
              onClick={() => toggleOption(option)}
              className="w-full flex items-center gap-3 py-3 text-left transition-opacity"
              style={{
                opacity: isGreyedOut ? 0.4 : 1,
                borderBottom: i < current.options.length - 1 ? '1px solid rgba(128,128,128,0.15)' : 'none',
              }}
            >
              {/* Radio / checkbox indicator */}
              <span
                className="flex-shrink-0 flex items-center justify-center rounded-md transition-colors"
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: isSingleSelect ? '50%' : '6px',
                  border: isSelected
                    ? 'none'
                    : '1.5px solid rgba(128,128,128,0.4)',
                  backgroundColor: isSelected
                    ? 'var(--text-blue, #3b82f6)'
                    : 'transparent',
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                {isSelected ? (
                  isSingleSelect ? (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )
                ) : (
                  <span style={{ color: 'var(--text-secondary, #9ca3af)' }}>{i + 1}</span>
                )}
              </span>
              <span
                className="text-sm"
                style={{ color: 'var(--text-primary)' }}
              >
                {option}
              </span>
            </button>
          );
        })}
      </div>

      {/* Custom input */}
      {allowCustom && (
        <div
          className="mx-5 mb-3 flex items-center gap-3 rounded-xl px-3 py-2.5"
          style={{
            backgroundColor: answer.custom.trim()
              ? 'rgba(59,130,246,0.1)'
              : 'rgba(128,128,128,0.08)',
            border: answer.custom.trim()
              ? '1.5px solid var(--text-blue, #3b82f6)'
              : '1.5px solid transparent',
          }}
        >
          <span
            className="flex-shrink-0 flex items-center justify-center"
            style={{
              width: 28,
              height: 28,
              borderRadius: isSingleSelect ? '50%' : '6px',
              backgroundColor: answer.custom.trim()
                ? 'var(--text-blue, #3b82f6)'
                : 'transparent',
              border: answer.custom.trim()
                ? 'none'
                : '1.5px solid rgba(128,128,128,0.4)',
            }}
          >
            {answer.custom.trim() ? (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary, #9ca3af)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            )}
          </span>
          <input
            type="text"
            value={answer.custom}
            onChange={(e) => setCustom(e.target.value)}
            placeholder="Something else"
            className="flex-1 bg-transparent text-sm outline-none"
            style={{ color: 'var(--text-primary)' }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (isLast) handleSubmit();
                else goNext();
              }
            }}
          />
          {/* Arrow / submit for custom input on non-last questions */}
          {!isLast && answer.custom.trim() && (
            <button
              onClick={goNext}
              className="flex-shrink-0 p-1.5 rounded-lg transition-opacity hover:opacity-80"
              style={{ color: 'var(--text-primary)' }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </button>
          )}
        </div>
      )}

      {/* Footer: selection count + skip + submit */}
      <div className="flex items-center justify-between px-5 pb-4 pt-1">
        <div className="text-xs" style={{ color: 'var(--text-blue, #3b82f6)' }}>
          {!isSingleSelect && answer.selected.length > 0 && (
            `${answer.selected.length} selected`
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={isLast ? handleSubmit : goNext}
            className="px-4 py-1.5 rounded-lg text-sm font-medium transition-opacity hover:opacity-80"
            style={{
              backgroundColor: 'rgba(128,128,128,0.15)',
              color: 'var(--text-primary)',
            }}
          >
            Skip
          </button>
          {(isLast || hasAnswer) && (
            <button
              onClick={isLast ? handleSubmit : goNext}
              className="p-2 rounded-lg transition-opacity hover:opacity-80"
              style={{
                backgroundColor: hasAnswer ? 'var(--accent-color, #c4704b)' : 'rgba(128,128,128,0.15)',
                color: hasAnswer ? '#fff' : 'var(--text-secondary)',
              }}
              aria-label={isLast ? 'Submit' : 'Next'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                {isLast ? (
                  <polyline points="5 12 12 5 12 19" />
                ) : (
                  <>
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </>
                )}
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QnACard;
